/**
 * S11 System, reduced to the part that is real: the API's own health.
 *
 * **This screen exists to prove the contract end to end.** Everything else in task 29 can be
 * true on a machine with no service running — the scaffold builds, the types compile, the
 * shell renders. None of that establishes that a generated method reaches a real handler and
 * comes back as the type it claims. One live call does, and `/health/deep` is the right one
 * to make it with: `API-Spec.md` has it unauthenticated by design, so it works before task
 * 30 builds login, and it reports the two dependencies whose absence is the likeliest reason
 * an operator is on this screen at all.
 *
 * The rest of S11 — engine components, queue depths, rate-limit headroom — needs M2.
 * `UI-Design-Spec.md#s11` describes fourteen panels; three are answerable today and eleven
 * would be fabricated, so eleven are absent and the placeholder copy says why.
 *
 * **The log viewer is the third, and it arrived ahead of M2 because it needed no model.** The
 * other eleven are blocked on state this platform does not record yet; a log tail is blocked on
 * a transport, and `jmt_logging.publish` is that transport. It is deliberately *only* a tail —
 * see `LogTerminal.tsx` — because the panel the spec describes has a time filter, and a time
 * filter implies a store that does not exist. Shipping the live half now and saying plainly
 * that it is the live half beats shipping nothing, and beats shipping something that looks like
 * an archive.
 *
 * Three design rules this screen is the easiest place to break:
 *
 * - **One hero figure per screen.** The health word is it, at `--num-display`. Nothing else
 *   here may be a hero.
 * - **Colour never means anything alone.** The word is spelled out — `Healthy` / `Degraded` /
 *   `Down` — and each dependency row carries a `yes` / `no` label, not just a tint.
 * - **`—` is not zero.** A dependency whose state is unknown because the request failed is
 *   `—`, never `false`; those are different claims and only one of them is a measurement.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '../api/client';
import type { Timeframe } from '../api/generated';
import { EmptyState } from '../design/components/messages/EmptyState.jsx';
import { ErrorPanel } from '../design/components/messages/ErrorPanel.jsx';
import { Skeleton } from '../design/components/messages/Skeleton.jsx';
import { StatusMatrixCell } from '../design/components/data/StatusMatrixCell.jsx';
import { TabBar } from '../design/components/chrome/TabBar.jsx';
import { DataHealthMatrix } from './DataHealthMatrix';
import { MODE_LABELS, preciseClockOf, useTimestampMode } from '../ui/timestamps';
import { LogTerminal } from './LogTerminal';
import { PLACEHOLDER_COPY } from './Placeholder';

/**
 * The dependencies `/health/deep` reports.
 *
 * Name and detail are separate fields rather than one string with an em dash in it, because the
 * header lays them out in columns — a name that carries its own explanation cannot be aligned
 * against the one below it, and two ragged rows is what made the old panel read as prose.
 */
const DEPENDENCY_LABELS: Record<string, { name: string; detail: string }> = {
  database: { name: 'Postgres', detail: 'the database answers' },
  keyring: { name: 'Key ring', detail: 'the KEK can unwrap the active DEK' },
};

/** The verdict word as a styling hook. Healthy is uncoloured, per `UI-Design-Spec.md#s11`. */
function stateOf(word: string): string {
  if (word === 'Healthy') return 'ok';
  if (word === 'Down') return 'down';
  if (word === '—') return 'unknown';
  return 'degraded';
}

function depState(value: boolean | undefined): string {
  if (value === undefined) return 'unknown';
  return value ? 'ok' : 'down';
}

/**
 * The legend, and it is not optional.
 *
 * `HeatmapCell`'s own rule, which applies to every coloured grid in this product: *"A heatmap
 * whose cells cannot be read without the legend is decoration."* Four states, each spelled
 * out, because colour never means anything alone.
 */
function Legend(): React.JSX.Element {
  return (
    <ul className="jmt-legend">
      <LegendItem swatch={<StatusMatrixCell state="fresh" size={12} />} label="fresh" />
      <LegendItem swatch={<StatusMatrixCell state="ageing" size={12} />} label="ageing — next bar overdue" />
      <LegendItem swatch={<StatusMatrixCell state="stale" size={12} />} label="stale — the engine would refuse it" />
      <LegendItem swatch={<StatusMatrixCell gap size={12} />} label="hatched — unfilled gap" />
      {/* Square and unrounded, matching both `StatusMatrixCell` and the mark it stands for
          in the matrix itself. It previously carried a `--radius-sm` that is not a token. */}
      <LegendItem swatch={<span className="jmt-legend__empty" />} label="no series stored" />
    </ul>
  );
}

function LegendItem({ swatch, label }: { swatch: React.ReactNode; label: string }): React.JSX.Element {
  return (
    <li className="jmt-legend__item">
      {swatch}
      <span>{label}</span>
    </li>
  );
}

function verdict(status: string | undefined, failed: boolean): string {
  if (failed) return 'Down';
  if (status === 'ok') return 'Healthy';
  if (status === undefined) return '—';
  return 'Degraded';
}

/**
 * The screen's two tabs.
 *
 * `Overview` is the URL `/system`, not `/system/overview` — the tab an operator lands on by
 * typing the screen's own name must be the screen's own address, or every link and every
 * bookmark to `/system` becomes a redirect.
 */
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'logs', label: 'Live log' },
];

export function SystemScreen(): React.JSX.Element {
  const timestampMode = useTimestampMode();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  // An unknown `:tab` shows the overview rather than an empty frame. The route only matches one
  // segment, so this is a typed URL, and the overview is the honest answer to "System".
  const active = tab === 'logs' ? 'logs' : 'overview';

  // Task 32. One row per series that has bars; the matrix fills in the combinations that
  // have none, because a timeframe that was never aggregated is the finding.
  const dataHealth = useQuery({
    queryKey: ['data-health'],
    queryFn: () => api.marketData.dataHealth({}),
    // Slower than the health poll above: this is a scan over the whole bars hypertable and
    // a series does not change freshness between one glance and the next.
    refetchInterval: 60_000,
  });

  const health = useQuery({
    queryKey: ['health', 'deep'],
    queryFn: () => api.system.healthDeep(),
    // The one place a refetch interval is right: this screen is watched during an incident,
    // and a stale "Healthy" is the single most misleading thing it could show.
    refetchInterval: 10_000,
  });

  // **Not `health.data?.checks`.** TanStack Query keeps the last successful `data` alongside
  // `isError`, so on a failed refetch the rows below would keep rendering the previous
  // answer — `yes / Postgres` under a hero reading `Down`, with nothing saying the booleans
  // are minutes old. Found by killing the service with this screen open, which is the exact
  // situation it exists for. `undefined` renders as `—`: absence has its own mark, and
  // "we did not get an answer" is not the same claim as "the answer was no".
  const checks = health.isError ? undefined : health.data?.checks;
  const word = verdict(health.data?.status, health.isError);

  return (
    <div className="jmt-screen jmt-screen--capped">
      {/* Navigation first, directly under the top bar, so it is in the same place on both tabs
          and does not move when the content below it changes height. */}
      <TabBar
        tabs={TABS}
        active={active}
        onSelect={(id) => navigate(id === 'overview' ? '/system' : `/system/${id}`)}
      />

      {/* 🔴 **A strip, not a hero.** `UI-Design-Spec.md#s11` asks for the health word at
          `--num-display` as the screen's one hero figure, and the operator has now rejected
          that twice as built — "really ugly", then "still too big". His call: he wrote the
          spec, and the argument for the hero was that the word is the only question S11
          answers, which stopped being true once the screen grew a log tab.

          So it is one dense row: state dot, the word, the version, and the two dependency
          checks inline. Everything the old block said, at a fifth of the height, and it reads
          as instrumentation rather than as a headline. Colour still appears only in the
          degraded and down cases, and the word is still spelled out beside the dot — a dot
          alone would be colour carrying meaning on its own.

          **Overview only.** Per the operator: not wanted while the terminal is running. The
          error panel below is the exception and stays on both. */}
      {active === 'overview' ? (
        health.isPending ? (
          <Skeleton height={38} width={520} />
        ) : (
          <div className="jmt-health" data-state={stateOf(word)}>
            <span className="jmt-health__dot" aria-hidden="true" />
            <span className="jmt-health__word">{word}</span>
            <span className="jmt-health__context">
              {health.isError
                ? 'the service did not answer'
                : `${health.data?.service} ${health.data?.version} · every 10s`}
            </span>

            <span className="jmt-health__deps">
              {Object.entries(DEPENDENCY_LABELS).map(([key, { name, detail }]) => {
                const state = checks?.[key];
                return (
                  <span
                    key={key}
                    className="jmt-health__dep"
                    data-state={depState(state)}
                    title={detail}
                  >
                    <span className="jmt-health__dep-name">{name}</span>
                    {/* `—` is not `no`. A dependency whose state is unknown because the request
                        failed is a different claim from one that answered false, and only one
                        of them is a measurement. */}
                    <span className="jmt-health__dep-state">
                      {state === undefined ? '—' : state ? 'yes' : 'no'}
                    </span>
                  </span>
                );
              })}
            </span>
          </div>
        )
      ) : null}

      {active === 'overview' && !health.isError && health.data?.reason ? (
        <p className="jmt-note jmt-note--secondary">{health.data.reason}</p>
      ) : null}

      {/* Not inside the overview tab: an API that is not answering is the reason the operator
          is here, and hiding that behind a tab they are not on is the one failure this screen
          cannot afford. It shows on both. */}
      {health.isError ? (
        <ErrorPanel
          message="The API did not answer its health check."
          detail={
            health.error instanceof Error
              ? health.error.message
              : String(health.error ?? 'unknown error')
          }
          onRetry={() => void health.refetch()}
        />
      ) : null}

      {active === 'logs' ? <LogTerminal /> : <>

      <section className="jmt-panel">
        <h2 className="jmt-section-heading">Market data</h2>
        {dataHealth.isPending ? (
          <Skeleton height={180} width={420} />
        ) : dataHealth.isError ? (
          <ErrorPanel
            message="Could not read data health."
            detail={
              dataHealth.error instanceof Error
                ? dataHealth.error.message
                : String(dataHealth.error)
            }
            onRetry={() => void dataHealth.refetch()}
          />
        ) : dataHealth.data.series.length === 0 ? (
          <EmptyState>
            No series stored. `jmt-marketdata` writes bars; until it has run against this
            database there is nothing to report on.
          </EmptyState>
        ) : (
          <>
            <DataHealthMatrix
              series={dataHealth.data.series}
              onOpen={(instrumentId: number, timeframe: Timeframe) =>
                navigate(`/markets?instrument=${instrumentId}&timeframe=${timeframe}`)
              }
            />
            <Legend />
            <p className="jmt-note" style={{ marginTop: 'var(--space-12)' }}>
              {dataHealth.data.series.length} series across{' '}
              {new Set(dataHealth.data.series.map((s) => s.symbol)).size} symbols, checked{' '}
              {/* Follows the header's mode, and the mode is named rather than assumed — the word
                  after the clock used to be a hardcoded `UTC`, which would have become a lie the
                  moment the toggle existed. */}
              {preciseClockOf(dataHealth.data.checked_at, timestampMode).slice(0, 8)}{' '}
              {MODE_LABELS[timestampMode]}.{' '}
              <strong>This lists the series that exist, not the ones that should.</strong> Nothing
              records which symbols are meant to be ingested — universes are not built — so a
              symbol absent from every row is a finding this matrix cannot make.
            </p>
          </>
        )}
      </section>

      <p className="jmt-note">{PLACEHOLDER_COPY['system']}</p>
      </>}
    </div>
  );
}
